import { countries } from './resources/countries';
import { v4 as uuidv4 } from 'uuid';
import logger from './logger';

const MONTHS: any = {
    'es': ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    'en': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    'pt': ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
}

const GENDER: any = {
    'es': {
        'M': 'Masculino',
        'F': 'Femenino'
    },
    'en': {
        'M': 'Male',
        'F': 'Female',
    },
    'pt': {
        'M': 'Homem',
        'F': 'Mulher'
    }
}

const SMOKER: any = {
    'es': {
        true: 'Fumador',
        false: 'No Fumador'
    },
    'en': {
        true: 'Smoker',
        false: 'Non Smoker'
    },
    'pt': {
        true: 'Sim',
        false: 'Não'
    }
}

export function formatNumberWithComma(number: number): string {
    if (!number) return '';
    //redondear a dos dígitos decimales
    const roundNumber = Math.round(number * 100) / 100;
    return roundNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatNumberWithDot(number: number): string {
    if (!number) return '';
    //redondear a dos dígitos decimales
    const roundNumber = Math.round(number * 100) / 100;
    return roundNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

// BRAZ-343: single source of truth for the USD currency prefix across all countries.
// Standardized to "US$ " (with trailing space). Switching to "US$" (no space) is a one-line change here.
export const USD_PREFIX = 'US$ ';

// USD amounts shown with the standardized prefix. Number formatting is unchanged
// (caller picks dot/comma via useDot); only the currency prefix is centralized here.
export function formatUsd(amount: number, useDot = false): string {
    return USD_PREFIX + (useDot ? formatNumberWithDot(amount) : formatNumberWithComma(amount));
}

export function formatDate(date: string, lang: string, useNumericMonth: boolean = false): string {
    if (date === '' || date === '-') return '';
    if (!date) return '';

    // Validar el formato DD-MM-YYYY
    const dateFormatRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/;
    if (!dateFormatRegex.test(date)) return '';

    const [day, month, year] = date.split('-');
    if (useNumericMonth) {
        return `${day}/${month}/${year}`;
    }
    const monthIndex = parseInt(month) - 1;
    const formattedMonth = MONTHS[lang][monthIndex];
    return `${day} / ${formattedMonth} / ${year}`;
}

export function formatDateLatin(date: string, lang: string): string {
    if (date === '' || date === '-') return '';
    if (!date) return '';

    // Validar el formato DD-MM-YYYY
    const dateFormatRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/;
    if (!dateFormatRegex.test(date)) return '';

    const [day, month, year] = date.split('-');
    const monthIndex = parseInt(month);
    //convertir monthIndex a string y agregarle un 0 al inicio si es menor a 10
    const monthString = monthIndex < 10 ? `0${monthIndex}` : `${monthIndex}`;
    //const upperCaseMonth = formattedMonth.toUpperCase();
    return `${day} / ${monthString} / ${year}`;
}

export function formatGender(value: string, lang: string): string {
    if (!value) return '';
    return GENDER[lang][value];
}

export function getCountryName(countryCode: string | undefined): string {
    if (countryCode === undefined) return '';
    logger.log('[utils]', 'countryCode', countryCode);
    if (!countryCode) return '';
    //const COUNTRIES_NOT_CARIBBEAN = ['AR','BO','BR','CL','CO','CR','DO','EC','SV','GT','HN','MX','NI','PA','PE','PY','UY','VE'];
    //const optionsFilter = countries.map((country: { code: any; }) => COUNTRIES_NOT_CARIBBEAN.includes(country.code) ? country : {...country, code: 'Caribbean'});
    //logger.log('[utils]', 'optionsFilter', optionsFilter);
    //const optionsFinal = optionsFilter?.map((country: { code: string; name: any; flag: string }) => ({ value: country.code, label: country.name, icon: country.flag })) || []
    //logger.log('[utils]', 'optionsFinal', optionsFinal);
    const country = countries.find((c: any) => c.code === countryCode);
    //logger.log('[utils]', 'country', country);
    return country ? country.name : '';
}

export function formatSmoker(value: boolean | undefined, lang: string): string {
    if (value === undefined) return '';
    return SMOKER[lang][value?.toString()];
}

export const generarUuid = () => {
    const nuevoUuid = uuidv4();
    return nuevoUuid;
};

export function formatCurrency(value: number | undefined | null, language: 'en' | 'es' | 'pt' = 'es'): string {
    if (value === undefined || value === null) {
        return language === 'pt' ? "R$ 0" : `${USD_PREFIX}0`;
    }
    
    // Para portugués, usar reales (R$) con coma para decimales y punto para miles
    if (language === 'pt') {
        // Convierte el número a formato brasileño: 1234.56 -> 1.234,56
        return `R$ ${value.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
    }
    
    // Para inglés y español, mantener USD con punto para decimales y coma para miles
    return `${USD_PREFIX}${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

export function getFrecuencyLabel(frecuency: string | undefined, language: 'en' | 'es' | 'pt' = 'es') {
    const labels = {
        'es': {
            'A': 'Anual',
            'S': 'Semestral',
            'T': 'Trimestral',
            'Q': 'Trimestral',
            'M': 'Mensual'
        },
        'en': {
            'A': 'Annual',
            'S': 'Semi-annual',
            'T': 'Quarterly',
            'Q': 'Quarterly',
            'M': 'Monthly'
        },
        'pt': {
            'A': 'Anual',
            'S': 'Semestral',
            'T': 'Trimestral',
            'Q': 'Trimestral',
            'M': 'Mensal'
        }
    };

    return labels[language][frecuency as keyof typeof labels['es']] || labels[language]['M'];
};

export const splitStringToArray = (commaSeparatedString: string | undefined): string[] => {
    if (commaSeparatedString === undefined) {
        return [];
    }
    // Return empty array if string is empty or undefined
    if (!commaSeparatedString) return [];

    // Split the string by comma and trim whitespace from each element
    return commaSeparatedString.split(',').map(item => item.trim());
}

export function convertBinaryToYesNo(value: string, language: 'en' | 'es' | 'pt' = 'es'): string {
    const responses: Record<'en' | 'es' | 'pt', Record<'0' | '1', string>> = {
        'es': { '1': 'Sí', '0': 'No' },
        'en': { '1': 'Yes', '0': 'No' },
        'pt': { '1': 'Sim', '0': 'Não' }
    };
    return responses[language][value as '0' | '1'] || responses[language]['0'];
}

export function getCurrentlyText(language: 'en' | 'es' | 'pt' = 'es'): string {
    const texts: Record<'en' | 'es' | 'pt', string> = {
        'es': 'actualmente',
        'en': 'currently',
        'pt': 'atualmente'
    };
    return texts[language];
}

/**
 * Calcula la edad basada en la fecha de nacimiento
 * @param dateOfBirth Fecha de nacimiento en formato DD-MM-YYYY
 * @returns Edad en años
 */
export function calculateAge(dateOfBirth: string): number {
    if (!dateOfBirth || dateOfBirth === '' || dateOfBirth === '-') return 0;
    
    // Validar el formato DD-MM-YYYY
    const dateFormatRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/;
    if (!dateFormatRegex.test(dateOfBirth)) return 0;

    const [day, month, year] = dateOfBirth.split('-');
    const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    
    return age;
}

/**
 * Calcula qué términos están disponibles basado en la edad del usuario
 * @param age Edad del usuario
 * @param availableTerms Lista de términos disponibles
 * @returns Lista de términos válidos (edad + término <= 75)
 */
export function getValidTerms(age: number, availableTerms: number[] = [10, 15, 20, 30]): number[] {
    const MAX_AGE = 75;
    return availableTerms.filter(term => age + term <= MAX_AGE);
}

/**
 * Encuentra el término máximo válido para una edad dada
 * @param age Edad del usuario
 * @param availableTerms Lista de términos disponibles
 * @returns El término máximo válido o null si ninguno es válido
 */
export function getMaxValidTerm(age: number, availableTerms: number[] = [10, 15, 20, 30]): number | null {
    const validTerms = getValidTerms(age, availableTerms);
    return validTerms.length > 0 ? Math.max(...validTerms) : null;
}

